import { fitWithin } from '@shared/image/fit'

/**
 * A foto de um produto, encolhida para virar miniatura.
 *
 * ## Por que os números são OUTROS, e não os do cupom
 *
 * A foto do cupom precisa continuar **legível** — é evidência, e vira entrada de
 * um modelo. Daí 1600 px e JPEG. A foto de um produto serve para **reconhecer a
 * embalagem numa lista**: 800 px é generoso até em tela retina, e o que se
 * ganha em bytes não custa nada de utilidade. É quatro vezes mais leve.
 *
 * ## "Todos os algoritmos": codifica em cada um e FICA COM O MENOR
 *
 * AVIF, WebP e JPEG são tentados, e vence o menor arquivo que o navegador
 * realmente produziu. Não é escolha por tabela: o resultado depende da imagem —
 * foto de embalagem com fundo liso comprime muito melhor em AVIF, e uma foto
 * ruidosa às vezes empata.
 *
 * Aqui a escolha é livre, ao contrário do cupom: essa imagem só é exibida pelo
 * **nosso** app num navegador, e não entra em modelo de IA nenhum. É por isso
 * que a nota do cupom recusou WebP e esta aceita AVIF.
 *
 * ## `toBlob` MENTE quando não conhece o formato, e essa é a armadilha
 *
 * Pedir `image/avif` a um navegador sem codificador AVIF **não dá erro**: ele
 * devolve um PNG, calado — e PNG de foto é maior que o JPEG que a gente queria
 * evitar. Por isso cada candidato é conferido pelo `blob.type` antes de entrar
 * na disputa. Sem essa checagem, "usar o melhor algoritmo" produziria o pior
 * arquivo.
 *
 * ## Falhar nunca impede o upload
 *
 * Mesma regra de `prepare-image`: qualquer erro devolve o original. Uma foto que
 * não sobe porque o otimizador tropeçou é pior do que uma foto grande.
 */

/** O maior lado. Ver o cabeçalho para por que 800 e não 1600. */
export const LADO_MAXIMO = 800

/**
 * Os candidatos, do mais eficiente ao mais compatível.
 *
 * As qualidades não são iguais de propósito: a escala de cada codificador é
 * própria, e 0,5 em AVIF é aproximadamente 0,75 em WebP em qualidade percebida.
 */
const CANDIDATOS: { tipo: string; qualidade: number }[] = [
  { tipo: 'image/avif', qualidade: 0.5 },
  { tipo: 'image/webp', qualidade: 0.75 },
  { tipo: 'image/jpeg', qualidade: 0.8 },
]

const TIMEOUT_MS = 15_000

export type FotoPreparada = {
  blob: Blob
  bytesOriginais: number
  bytesFinais: number
  /** Qual codificador venceu. A tela pode dizer, e o log guarda. */
  formato: string
  processada: boolean
}

export async function preparePhoto(file: Blob): Promise<FotoPreparada> {
  const original: FotoPreparada = {
    blob: file,
    bytesOriginais: file.size,
    bytesFinais: file.size,
    formato: file.type,
    processada: false,
  }

  try {
    const canvas = document.createElement('canvas')
    if (typeof canvas.getContext !== 'function') return original
    const ctx = canvas.getContext('2d')
    if (ctx === null) return original

    const img = await carregar(file)
    const alvo = fitWithin(img.naturalWidth, img.naturalHeight, LADO_MAXIMO)
    if (alvo.width <= 0 || alvo.height <= 0) return original

    canvas.width = alvo.width
    canvas.height = alvo.height
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(img, 0, 0, alvo.width, alvo.height)

    // Codifica em todos, descarta os que o navegador não soube fazer, e fica
    // com o menor. Ver o cabeçalho.
    const resultados = await Promise.all(
      CANDIDATOS.map(async ({ tipo, qualidade }) => {
        const blob = await new Promise<Blob | null>((resolve) => {
          canvas.toBlob(resolve, tipo, qualidade)
        })
        // **A checagem que impede o PNG silencioso.** Ver o cabeçalho.
        return blob !== null && blob.size > 0 && blob.type === tipo ? blob : null
      }),
    )

    const menor = resultados
      .filter((b): b is Blob => b !== null)
      .sort((a, b) => a.size - b.size)[0]

    // Nenhum formato saiu, ou o melhor ficou maior que o original: fica o que
    // veio. Acontece com imagem já otimizada — a da base pública, por exemplo,
    // que chega com 17 KB.
    if (menor === undefined || menor.size >= file.size) return original

    return {
      blob: menor,
      bytesOriginais: file.size,
      bytesFinais: menor.size,
      formato: menor.type,
      processada: true,
    }
  } catch (erro: unknown) {
    console.error('preparePhoto: não deu para processar, subindo o original', erro)
    return original
  }
}

/**
 * Baixa uma imagem de outra origem para poder reprocessá-la.
 *
 * **Existe para não depender da URL de terceiro.** Guardar o link da base
 * pública seria mais barato e deixaria a foto do catálogo apagar no dia em que
 * eles mudarem o caminho — e faria cada abertura da lista bater no servidor
 * deles.
 *
 * Devolve `null` em qualquer falha: a foto é um extra, e nenhuma falha dela pode
 * impedir o produto de existir.
 */
export async function baixarImagem(url: string, fetchImpl: typeof fetch = fetch): Promise<Blob | null> {
  try {
    const resposta = await fetchImpl(url)
    if (!resposta.ok) return null
    const blob = await resposta.blob()
    return blob.type.startsWith('image/') ? blob : null
  } catch (erro: unknown) {
    console.error('baixarImagem: não deu para baixar', erro)
    return null
  }
}

/** Decodifica com teto de tempo. Espelha `prepare-image`. */
function carregar(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    const limpar = () => {
      clearTimeout(relogio)
      URL.revokeObjectURL(url)
    }
    const relogio = setTimeout(() => {
      limpar()
      reject(new Error('preparePhoto: a imagem não decodificou a tempo'))
    }, TIMEOUT_MS)

    img.onload = () => {
      limpar()
      resolve(img)
    }
    img.onerror = () => {
      limpar()
      reject(new Error('preparePhoto: o navegador não decodificou esta imagem'))
    }
    img.src = url
  })
}
